<template>
  <modal
    elem-id="modal_so-catalog-fiscal"
    :show="isVisible"
    :title="modalTitle"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="isSaving ? t('Salvando...') : t('Salvar')"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div class="origin-row">
        <status-tag
          v-if="originState === 'missing'"
          severity="danger"
          :value="t('Não configurado')"
          data-testid="so-catalog-fiscal-origin-missing"
        />
        <status-tag
          v-else-if="originState === 'override'"
          severity="warn"
          :value="t('Override da franquia')"
          :title="t('Existe uma linha fiscal desta franquia. Cada campo usa o valor da franquia quando preenchido; senão, o global.')"
          data-testid="so-catalog-fiscal-origin-override"
        />
        <status-tag
          v-else-if="originState === 'global'"
          severity="info"
          :value="t('Global')"
          data-testid="so-catalog-fiscal-origin-global"
        />
      </div>

      <s-o-catalog-fiscal-service-form
        v-if="kind === 'service'"
        ref="formRef"
        :item-id="itemId"
        :franchise-id="franchiseId"
        :scope="scope"
        :effective="serviceEffective"
        @submit-success="handleFormSuccess"
      />
      <s-o-catalog-fiscal-product-form
        v-else-if="kind === 'product'"
        ref="formRef"
        :item-id="itemId"
        :franchise-id="franchiseId"
        :scope="scope"
        :effective="productEffective"
        @submit-success="handleFormSuccess"
      />
      <s-o-catalog-fiscal-additional-service-form
        v-else-if="kind === 'additional_service'"
        ref="formRef"
        :item-id="itemId"
        :franchise-id="franchiseId"
        :scope="scope"
        :effective="additionalServiceEffective"
        @submit-success="handleFormSuccess"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOCatalogFiscal, type FiscalScope } from '../../composables/useSOCatalogFiscal';
import type { So_ServiceFiscalEffectiveRow } from '../../contracts/So_ServiceFiscalEffectiveRow';
import type { So_ProductFiscalEffectiveRow } from '../../contracts/So_ProductFiscalEffectiveRow';
import type { So_AdditionalServiceFiscalEffectiveRow } from '../../contracts/So_AdditionalServiceFiscalEffectiveRow';
import SOCatalogFiscalServiceForm from './SOCatalogFiscalServiceForm.vue';
import SOCatalogFiscalProductForm from './SOCatalogFiscalProductForm.vue';
import SOCatalogFiscalAdditionalServiceForm from './SOCatalogFiscalAdditionalServiceForm.vue';

type Id = number | string;
type Kind = 'service' | 'product' | 'additional_service';
type EffectiveRow = So_ServiceFiscalEffectiveRow | So_ProductFiscalEffectiveRow | So_AdditionalServiceFiscalEffectiveRow;

interface FormInstance {
  handleSubmit: () => Promise<void>;
}

interface ModalOpenPayload {
  kind: Kind;
  id: Id;
  franchiseId?: number;
  scope: FiscalScope;
}

const { t } = useI18n();
const { getServiceFiscal, getProductFiscal, getAdditionalServiceFiscal } = useSOCatalogFiscal();

const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const kind = ref<Kind>('service');
const itemId = ref<Id | null>(null);
const franchiseId = ref<number | undefined>(undefined);
const scope = ref<FiscalScope>('franquia');
const effective = ref<EffectiveRow | null>(null);
const formRef = ref<FormInstance | null>(null);

// Narrowed views for the two form components — `effective` itself stays a
// union (originState below reads only the fields common to both Effective
// contracts), but each form expects its own concrete row type.
const serviceEffective = computed<So_ServiceFiscalEffectiveRow | null>(() =>
  kind.value === 'service' ? (effective.value as So_ServiceFiscalEffectiveRow | null) : null
);
const productEffective = computed<So_ProductFiscalEffectiveRow | null>(() =>
  kind.value === 'product' ? (effective.value as So_ProductFiscalEffectiveRow | null) : null
);
const additionalServiceEffective = computed<So_AdditionalServiceFiscalEffectiveRow | null>(() =>
  kind.value === 'additional_service' ? (effective.value as So_AdditionalServiceFiscalEffectiveRow | null) : null
);

const modalTitle = computed<string>(() => {
  const itemLabel =
    kind.value === 'service'
      ? t('Dados fiscais do serviço')
      : kind.value === 'product'
        ? t('Dados fiscais do produto')
        : t('Dados fiscais do serviço adicional');
  return scope.value === 'global'
    ? `${itemLabel} — ${t('GLOBAL (todas as franquias)')}`
    : `${itemLabel} — ${t('somente esta franquia')}`;
});

// `is_override` only means "a franchise-specific row exists" — it says
// nothing about which individual fields that row actually filled in (the
// merge is field-by-field; see the composable docblock and the "Override
// da franquia" tag's own tooltip below). This derivation is intentionally
// the same regardless of scope: reading with no franchise_id (global-edit
// mode) always yields is_override=false, which is the correct reading of
// the global row itself.
const originState = computed<'missing' | 'override' | 'global'>(() => {
  if (!effective.value || !effective.value.is_configured) return 'missing';
  return effective.value.is_override ? 'override' : 'global';
});

const loadEffective = async (): Promise<void> => {
  if (itemId.value == null) return;
  // Global-scope edit reads the global row alone (no franchise_id) so the
  // form pre-fills with the record actually being edited, not a merge.
  const effectiveFranchiseId = scope.value === 'global' ? undefined : franchiseId.value;
  try {
    effective.value =
      kind.value === 'service'
        ? await getServiceFiscal(Number(itemId.value), effectiveFranchiseId)
        : kind.value === 'product'
          ? await getProductFiscal(Number(itemId.value), effectiveFranchiseId)
          : await getAdditionalServiceFiscal(Number(itemId.value), effectiveFranchiseId);
  } catch (err) {
    logError('SOCatalogFiscalModal', 'loadEffective', 'Error', err);
    effective.value = null;
  }
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  if (!data) return;
  log('SOCatalogFiscalModal', 'handleModalOpen', 'Opening', data);
  kind.value = data.kind;
  itemId.value = data.id;
  franchiseId.value = data.franchiseId;
  scope.value = data.scope;
  effective.value = null;
  isVisible.value = true;
  await loadEffective();
};

const handleClose = (): void => {
  isVisible.value = false;
  isSaving.value = false;
  itemId.value = null;
  effective.value = null;
};

const handleSave = async (): Promise<void> => {
  if (!formRef.value) return;
  isSaving.value = true;
  try {
    await formRef.value.handleSubmit();
  } finally {
    isSaving.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOCatalogFiscalModal', 'handleFormSuccess', 'Saved');
  eventBus.emit('so-catalog-fiscal.saved');
  handleClose();
};

onMounted(() => {
  eventBus.on('modal.so-catalog-fiscal.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-catalog-fiscal.open', handleModalOpen);
});
</script>

<style scoped>
.origin-row {
  margin-bottom: 1rem;
}
</style>
